import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/ui/Button';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/Toast';
import { agencyMetaAdsApi } from '../api/agencyMetaAdsApi';
import { agencyKeys } from '../queryKeys';
import styles from './MetaAdAccountPicker.module.css';

interface Props {
  locationId: string;
}

export function MetaAdAccountPicker({ locationId }: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [picking, setPicking] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [selectedPageId, setSelectedPageId] = useState<string>('');

  const { data: status } = useQuery({
    queryKey: agencyKeys.metaLocationStatus(locationId),
    queryFn: () => agencyMetaAdsApi.getLocationStatus(locationId),
  });

  const { data: tokenStatus } = useQuery({
    queryKey: agencyKeys.metaTokenStatus(),
    queryFn: () => agencyMetaAdsApi.getTokenStatus(),
  });

  const tokenReady = tokenStatus?.hasToken && !tokenStatus.lastError;

  const { data: accounts, isLoading: accountsLoading } = useQuery({
    queryKey: agencyKeys.metaAvailableAccounts(),
    queryFn: () => agencyMetaAdsApi.getAvailableAccounts(),
    enabled: tokenReady === true && picking,
  });

  const { data: pages, isLoading: pagesLoading } = useQuery({
    queryKey: agencyKeys.metaAvailablePages(selectedId || undefined),
    queryFn: () => agencyMetaAdsApi.getAvailablePages(selectedId || undefined),
    enabled: tokenReady === true && picking && !!selectedId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: agencyKeys.metaLocationStatus(locationId) });
    queryClient.invalidateQueries({ queryKey: agencyKeys.metaAvailableAccounts() });
  };

  const assignMutation = useMutation({
    mutationFn: () => {
      const acc = accounts?.find((a) => a.id === selectedId);
      const page = pages?.find((p) => p.id === selectedPageId);
      if (!acc) throw new Error('Pick an ad account');
      if (!page) throw new Error('Pick a Facebook Page');
      return agencyMetaAdsApi.assignLocation(locationId, {
        adAccountId: acc.id,
        adAccountName: acc.name,
        pageId: page.id,
        pageName: page.name,
      });
    },
    onSuccess: () => {
      toast.success('Meta ad account assigned. Initial sync queued.');
      setPicking(false);
      setSelectedId('');
      setSelectedPageId('');
      invalidate();
    },
    onError: (err: Error) => toast.error(err.message ?? 'Failed to assign ad account.'),
  });

  const unassignMutation = useMutation({
    mutationFn: () => agencyMetaAdsApi.unassignLocation(locationId),
    onSuccess: () => {
      toast.success('Meta ad account unassigned.');
      invalidate();
    },
    onError: () => toast.error('Failed to unassign.'),
  });

  const syncMutation = useMutation({
    mutationFn: () => agencyMetaAdsApi.syncLocation(locationId),
    onSuccess: () => toast.success('Sync queued.'),
    onError: () => toast.error('Failed to queue sync.'),
  });

  if (!tokenStatus) return null;

  // Agency hasn't configured a token yet
  if (!tokenStatus.hasToken) {
    return (
      <div className={styles.row}>
        <span className={styles.label}>Meta:</span>
        <span className={styles.muted}>
          No agency token configured — set one up in the Meta Ads card above first
        </span>
      </div>
    );
  }

  // Token exists but is failing
  if (tokenStatus.lastError) {
    return (
      <div className={styles.row}>
        <span className={styles.label}>Meta:</span>
        <span className={styles.error}>Agency token error — fix it in the Meta Ads card above</span>
      </div>
    );
  }

  // Connected (but yield to the picker if the user is actively re-assigning)
  if (status?.connected && !picking) {
    return (
      <div className={styles.row}>
        <span className={styles.label}>Meta:</span>
        <span className={styles.assignedName}>{status.adAccountName ?? 'Connected'}</span>
        <span className={styles.assignedId}>{status.adAccountId}</span>
        {status.pageName ? (
          <span className={styles.assignedId}>· Page: {status.pageName}</span>
        ) : (
          <span className={styles.warning}>· no Page set — launches will fail</span>
        )}
        {status.lastSyncError && <span className={styles.warning}>· sync error</span>}
        <div className={styles.actionGroup}>
          <Button
            variant="ghost"
            size="sm"
            loading={syncMutation.isPending}
            onClick={() => syncMutation.mutate()}
          >
            Sync
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
            Change
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={unassignMutation.isPending}
            onClick={() => unassignMutation.mutate()}
          >
            Unassign
          </Button>
        </div>
      </div>
    );
  }

  // Picker open (either new assignment or changing)
  if (picking) {
    // Show all accounts; one already used by another location is still selectable (shared).
    const accountOptions = (accounts ?? []).map((a) => {
      const others = a.assignedLocations.filter((l) => l.id !== locationId);
      return {
        value: a.id,
        label:
          others.length > 0
            ? `${a.name} (${a.id}) — also used by ${others.map((l) => l.name).join(', ')}`
            : `${a.name} (${a.id})`,
      };
    });

    const pageOptions = (pages ?? []).map((p) => ({
      value: p.id,
      label: p.category ? `${p.name} (${p.category})` : p.name,
    }));

    return (
      <div className={styles.pickerRow}>
        <span className={styles.label}>Meta:</span>
        <div className={styles.select}>
          <Select
            value={selectedId}
            onChange={(e) => {
              setSelectedId(e.target.value);
              setSelectedPageId('');
            }}
            options={[
              { value: '', label: accountsLoading ? 'Loading…' : 'Pick an ad account' },
              ...accountOptions,
            ]}
            disabled={accountsLoading || assignMutation.isPending}
            fullWidth
          />
        </div>
        <div className={styles.select}>
          <Select
            value={selectedPageId}
            onChange={(e) => setSelectedPageId(e.target.value)}
            options={[
              {
                value: '',
                label: !selectedId
                  ? 'Pick an ad account first'
                  : pagesLoading
                    ? 'Loading pages…'
                    : pageOptions.length === 0
                      ? 'No pages found for this account'
                      : 'Pick a Facebook Page',
              },
              ...pageOptions,
            ]}
            disabled={!selectedId || pagesLoading || assignMutation.isPending}
            fullWidth
          />
        </div>
        <div className={styles.actionGroup}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setPicking(false);
              setSelectedId('');
              setSelectedPageId('');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={assignMutation.isPending}
            disabled={!selectedId || !selectedPageId}
            onClick={() => assignMutation.mutate()}
          >
            Assign
          </Button>
        </div>
      </div>
    );
  }

  // Not assigned, not picking
  return (
    <div className={styles.row}>
      <span className={styles.label}>Meta:</span>
      <span className={styles.unassigned}>Not assigned</span>
      <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
        Assign Ad Account
      </Button>
    </div>
  );
}
